import { z } from 'zod';
/**
 * procguard — Process Verification & Operator Guidance.
 *
 * The customer-facing object is a **Procedure** (SOP): the chronological sequence
 * of tasks a station worker performs at a workstation, authored step by step, that
 * the camera then follows and verifies. In plain language it is the "recipe" the
 * worker follows; the entity everywhere is Procedure. A live run of one is a Cycle
 * (Phase 2). This file is the shared contract — every shape is a Zod schema so the
 * same definition validates a request and produces the static type.
 *
 * Phase 1 covers the authored side only: workstations, procedures, ordered steps,
 * their expected components (part / quantity / bin) and regions of interest, plus
 * JSON import/export so a procedure authored once can be rolled out to identical
 * workstations. The cycle state machine and detectors arrive in Phase 2.
 */
// ── Enums ───────────────────────────────────────────────────────────────────
export const PROCEDURE_STATUSES = ['draft', 'published', 'archived'];
export const ProcedureStatusSchema = z.enum(PROCEDURE_STATUSES);
/** The operator action a step expects — the baseline detector maps to these. */
export const EXPECTED_ACTIONS = ['pick', 'place', 'fasten', 'scan', 'inspect', 'other'];
export const ExpectedActionSchema = z.enum(EXPECTED_ACTIONS);
/** What a step ROI is watching. */
export const ROI_KINDS = ['component', 'action', 'inspect'];
export const RoiKindSchema = z.enum(ROI_KINDS);
/**
 * How a step is verified:
 *   presence  — activity in the ROI (the baseline, no identity)
 *   component — a trained component model identifies the part by class
 *   label     — the Etiket-AI reader reads the label/barcode in the ROI and
 *               matches it to the step's expected part number (no training)
 */
export const VERIFY_MODES = ['presence', 'component', 'label'];
export const VerifyModeSchema = z.enum(VERIFY_MODES);
// ── Leaf shapes (reused by the JSON document and the DTOs) ──────────────────
/** A polygon vertex, normalised to the frame [0,1] — same convention as safety zones. */
export const RoiPointSchema = z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
});
export const StepRoiSchema = z.object({
    name: z.string().max(120).default(''),
    kind: RoiKindSchema.default('action'),
    /** Which camera this ROI is drawn on; null = the workstation's primary camera. */
    cameraId: z.number().int().positive().nullable().default(null),
    polygon: z.array(RoiPointSchema).min(3, 'an ROI needs at least three points'),
});
export const StepComponentSchema = z.object({
    partNumber: z.string().min(1).max(120),
    description: z.string().max(500).default(''),
    quantity: z.number().int().min(1).default(1),
    binLocation: z.string().max(120).nullable().default(null),
    /** Set by the server: the parts-master row this component resolved to. */
    partId: z.number().int().positive().nullable().default(null),
});
export const StepSchema = z.object({
    seq: z.number().int().min(1),
    name: z.string().min(1).max(200),
    instruction: z.string().max(4000).default(''),
    referenceImageKey: z.string().max(500).nullable().default(null),
    referenceClipKey: z.string().max(500).nullable().default(null),
    expectedAction: ExpectedActionSchema.default('other'),
    critical: z.boolean().default(false),
    timeoutS: z.number().min(0).nullable().default(null),
    /** How this step is verified — see VerifyModeSchema. 'label' matches the read
     *  label/barcode against this step's expected component part numbers. */
    verifyBy: VerifyModeSchema.default('presence'),
    /** Assessment targets (skill/time scoring): standard = excellent bar, acceptable
     *  = pass bar. Time taken is measured at run time and scored against these. */
    standardTimeS: z.number().min(0).nullable().default(null),
    acceptableTimeS: z.number().min(0).nullable().default(null),
    /** Steps sharing a group may be done in any order; null = strictly sequential. */
    parallelGroup: z.number().int().nullable().default(null),
    components: z.array(StepComponentSchema).default([]),
    rois: z.array(StepRoiSchema).default([]),
});
/**
 * The portable Procedure document — the unit of import/export. It deliberately
 * omits identity, workstation binding, version and status: those belong to the
 * place a document lands, not to the document. Author once, export, import onto
 * any number of identical workstations.
 */
export const ProcedureDocSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).default(''),
    cycleTimeTargetS: z.number().min(0).nullable().default(null),
    steps: z.array(StepSchema).min(1, 'a procedure needs at least one step'),
});
// ── Request bodies (authoring API) ──────────────────────────────────────────
/** A camera bound to a workstation; a workstation may have several (multi-angle). */
export const WorkstationCameraSchema = z.object({
    cameraId: z.number().int().positive(),
    label: z.string().max(120).default(''),
    isPrimary: z.boolean().default(false),
});
export const CreateWorkstationSchema = z.object({
    customerId: z.number().int().positive(),
    name: z.string().min(1).max(200),
    site: z.string().max(200).default(''),
    line: z.string().max(200).default(''),
    stationId: z.number().int().positive().nullable().default(null),
    /** The cameras this workstation watches; the whole set is replaced on update. */
    cameras: z.array(WorkstationCameraSchema).default([]),
    enabled: z.boolean().default(false),
    /** Per-workstation model cadence, so several can share one edge node. */
    frameIntervalMs: z.number().int().min(200).max(60_000).default(1000),
});
export const UpdateWorkstationSchema = CreateWorkstationSchema.partial().omit({ customerId: true });
export const CreateProcedureSchema = z.object({
    workstationId: z.number().int().positive(),
    name: z.string().min(1).max(200),
    description: z.string().max(2000).default(''),
    cycleTimeTargetS: z.number().min(0).nullable().default(null),
});
export const UpdateProcedureSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).optional(),
    cycleTimeTargetS: z.number().min(0).nullable().optional(),
    status: ProcedureStatusSchema.optional(),
});
/** Replace the whole ordered step list of a procedure (the authoring save). */
export const ReplaceStepsSchema = z.object({ steps: z.array(StepSchema).min(1) });
/** Import a procedure document onto a target workstation (creates a draft). */
export const ImportProcedureSchema = z.object({
    workstationId: z.number().int().positive(),
    procedure: ProcedureDocSchema,
});
// ── Pure helpers (unit-testable without a database) ─────────────────────────
/**
 * Reduce a procedure + its steps to the portable document, dropping identity and
 * binding. Steps are ordered by `seq`, and each step's components/ROIs are kept.
 * The inverse (document → rows) is the import path in the service.
 */
export function toProcedureDoc(procedure, steps) {
    return {
        name: procedure.name,
        description: procedure.description,
        cycleTimeTargetS: procedure.cycleTimeTargetS,
        steps: [...steps]
            .sort((a, b) => a.seq - b.seq)
            .map((s) => ({
            seq: s.seq,
            name: s.name,
            instruction: s.instruction,
            referenceImageKey: s.referenceImageKey,
            referenceClipKey: s.referenceClipKey,
            expectedAction: s.expectedAction,
            critical: s.critical,
            timeoutS: s.timeoutS,
            verifyBy: s.verifyBy,
            standardTimeS: s.standardTimeS,
            acceptableTimeS: s.acceptableTimeS,
            parallelGroup: s.parallelGroup,
            components: s.components.map((c) => ({ ...c })),
            rois: s.rois.map((r) => ({ ...r })),
        })),
    };
}
/**
 * Normalise a step list before it is written: re-number `seq` to a dense 1..n in
 * the given order, so an authoring UI can reorder freely and the store always
 * holds a clean sequence. Returns a new array.
 */
export function renumberSteps(steps) {
    return steps.map((s, i) => ({ ...s, seq: i + 1 }));
}
/**
 * Score one step the way the assessment screen does. Time is graded against the
 * standard (excellent) and acceptable bars; the count is graded against the
 * expected quantity. Evaluation passes only when the time is within acceptable
 * AND the count matches — the single source of truth for screen and report.
 */
export function assessStep(p) {
    const taken = p.timeTakenS ?? null;
    const std = p.standardTimeS ?? null;
    const acc = p.acceptableTimeS ?? null;
    let range = null;
    if (taken != null && std != null) {
        // Strictly under standard is excellent; at-or-under the acceptable bar passes.
        if (taken < std)
            range = 'excellent';
        else if (acc != null && taken <= acc)
            range = 'acceptable';
        else
            range = 'not_acceptable';
    }
    const countTracked = p.standardCount != null;
    const countOk = !countTracked || p.actualCount === p.standardCount;
    const timeOk = range === null ? true : range !== 'not_acceptable';
    const scored = taken != null || countTracked;
    const evaluation = !scored ? null : timeOk && countOk ? 'pass' : 'fail';
    let remark = '';
    if (!countOk)
        remark = `Count mismatch (${p.actualCount ?? '—'}/${p.standardCount})`;
    else if (range === 'excellent')
        remark = 'Completed in excellent time';
    else if (range === 'acceptable')
        remark = 'Completed within acceptable time';
    else if (range === 'not_acceptable')
        remark = 'Completed after set time';
    return { range, evaluation, remark };
}
/**
 * Human label for a cycle's stored status. The DB keeps the raw enum
 * (ok/nok/aborted/running); no screen should ever show "nok" to a person.
 */
export function cycleResultLabel(status) {
    const map = { ok: 'Pass', nok: 'Fail', aborted: 'Aborted', running: 'Running' };
    return map[status] ?? status;
}
// ── Parts master (per customer) ─────────────────────────────────────────────
export const CreatePartSchema = z.object({
    customerId: z.number().int().positive(),
    partNumber: z.string().min(1).max(120),
    description: z.string().max(500).default(''),
    unit: z.string().max(20).default('ea'),
    active: z.boolean().default(true),
});
export const UpdatePartSchema = CreatePartSchema.partial().omit({ customerId: true });
/** Bulk import a customer's parts list (upsert on part number). */
export const ImportPartsSchema = z.object({
    customerId: z.number().int().positive(),
    parts: z
        .array(z.object({
        partNumber: z.string().min(1).max(120),
        description: z.string().max(500).default(''),
        unit: z.string().max(20).default('ea'),
    }))
        .min(1),
});
// ── Cycles (runtime) ─────────────────────────────────────────────────────────
// String-literal unions that match the state machine's own types by value.
export const CYCLE_RESULTS = ['ok', 'nok', 'aborted'];
export const DEVIATION_KINDS = [
    'missing_component',
    'wrong_component',
    'misaligned_component',
    'step_skipped',
    'wrong_order',
    'step_overtime',
    'cycle_overtime',
    'kit_incomplete',
];
// ── Alarm targets (per workstation) — mirrors the safety alarm target shape ──
export const AlarmTargetSchema = z.object({
    name: z.string().max(120).default(''),
    url: z.string().url().max(500),
    method: z.enum(['GET', 'POST']).default('GET'),
    severities: z.array(z.enum(['info', 'warning', 'critical'])).default(['critical', 'warning']),
});
export const SetAlarmConfigSchema = z.object({ targets: z.array(AlarmTargetSchema).max(20) });
/** A supervisor clearing a blocked critical step — logged with who and why. */
export const OverrideSchema = z.object({
    seq: z.number().int().min(1),
    reason: z.string().min(3).max(500),
});
//# sourceMappingURL=procguard.js.map